"use client";

/**
 * ObservabilityBridge — connects auth state to the PostHog SDK.
 *
 * Sits below ``AuthProvider`` in the provider tree (see
 * ``plans/PLAN_POSTHOG_01_FOUNDATIONS.md`` § "Provider architecture")
 * so it can read ``useAuth()``. Purely declarative: subscribes via
 * ``useEffect`` to changes in the auth user and runs the right
 * ``identify`` / ``group`` / ``opt_in`` / ``opt_out`` / ``reset``
 * combination per § "Identity model" + § "Global-admin handling".
 *
 * Renders children as-is — the bridge is a passthrough, not a layout.
 *
 * Logout teardown is NOT handled here. ``posthog.reset()`` runs inside
 * ``src/lib/api/auth.ts`` so it happens synchronously after the network
 * round-trip, before any local state is cleared. See § "Logout flow
 * integration".
 */

import { useEffect, type ReactNode } from "react";

import type { MeResponseCompany } from "@/lib/api/schemas";
import { useAuth } from "@/lib/auth/context";
import { useObservability } from "@/lib/observability/context";

interface ObservabilityBridgeProps {
  children: ReactNode;
}

/**
 * Person Properties payload, shaped per Plan_01 § "Identity model" →
 * "Person Properties". Optional keys (timezone today; locale later)
 * are omitted entirely when null rather than sent as ``null`` — see
 * Plan_05 § "Property-value rules".
 */
interface PersonProperties {
  email: string;
  name: string;
  actor_kind: "company_admin" | "module_user";
  actor_roles: string[];
  user_modules: string[];
  user_status: string;
  mfa_enabled: boolean;
  last_login_at: string;
  timezone?: string;
}

/**
 * Group Properties payload, shaped per Plan_01 § "Identity model" →
 * "Group Properties". Same omit-when-null rule applies to ``country``
 * / ``timezone`` / ``sso_mode``.
 */
interface GroupProperties {
  name: string;
  slug: string;
  status: string;
  setup_complete: boolean;
  marketing_licenses: number | null;
  company_modules: string[];
  country?: string;
  timezone?: string;
  sso_mode?: string;
  pilot_cohort: string | null;
}

/**
 * Pilot-cohort cutoff per Plan_01 § "Group Properties":
 * companies created before 2026-08-31 belong to the pilot cohort
 * (cohort id = slug); companies created after the cutoff get null.
 *
 * Exported so tests can pin the cutoff without re-deriving the date.
 */
export const PILOT_COHORT_CUTOFF = new Date("2026-08-31T23:59:59Z");

export function derivePilotCohort(
  company: Pick<MeResponseCompany, "slug" | "created_at">,
): string | null {
  const createdAt = new Date(company.created_at);
  // Guard against an unparseable timestamp — return null rather than
  // attaching the slug to a future-dated company whose ``created_at``
  // parsed to ``NaN``. ``getTime()`` returns NaN for invalid dates;
  // every NaN comparison is false, so the < check below would treat it
  // as post-cutoff anyway, but being explicit makes the intent obvious.
  if (Number.isNaN(createdAt.getTime())) return null;
  return createdAt < PILOT_COHORT_CUTOFF ? company.slug : null;
}

export function ObservabilityBridge({ children }: ObservabilityBridgeProps) {
  const { posthog, isEnabled } = useObservability();
  const { user } = useAuth();

  useEffect(() => {
    // Bridge is a no-op when the SDK is disabled (env kill-switch or
    // missing key). Children still render — the bridge is a passthrough.
    if (!isEnabled || !posthog) return;
    if (!user) {
      // Anonymous / pre-login. The GA branch below calls
      // ``opt_out_capturing()``, which posthog-js persists to
      // ``localStorage`` under ``__ph_opt_in_out_<token>`` — independent
      // of our ``persistence: "memory"`` setting. That flag survives
      // logout and poisons this browser for every later visitor
      // (anonymous pre-login capture, and the next user until they log in
      // as a non-GA). Clear any stale opt-out here so a prior admin
      // session can't silently suppress all capture. No-op when no flag
      // is set; default consent is opt-in (opt_out_capturing_by_default
      // is false), so clearing restores normal capture.
      posthog.clear_opt_in_out_capturing();
      return;
    }

    if (user.isGlobalAdmin) {
      // Global admins are excluded from customer analytics per master
      // decision #2. opt_out blocks future capture calls; reset wipes
      // any anonymous distinct_id that was generated before auth
      // resolved (e.g. pre-auth pageviews on /login).
      posthog.opt_out_capturing();
      posthog.reset();
      return;
    }

    // Customer-side login. opt_in covers the rare GA → customer
    // account-switch in the same tab: a prior opt_out would otherwise
    // silently swallow every capture call until page reload.
    posthog.opt_in_capturing();

    const personProperties: PersonProperties = {
      email: user.email,
      name: user.fullName,
      actor_kind: user.isCompanyAdmin ? "company_admin" : "module_user",
      // The RBAC tier the user holds. ``actor_kind`` is the discriminator;
      // ``actor_roles`` is the list form (and the slot the granular named
      // role drops into once /auth/me carries ``role`` — backend MeResponse
      // does not expose it yet, so today this is the company-admin /
      // module-user tier). Global admins never reach here (opted out above).
      actor_roles: [user.isCompanyAdmin ? "company_admin" : "module_user"],
      user_modules: user.enabledModules,
      user_status: user.status,
      mfa_enabled: user.mfaEnabled,
      last_login_at: new Date().toISOString(),
    };
    if (user.timezone) {
      personProperties.timezone = user.timezone;
    }

    posthog.identify(user.userId, personProperties);

    if (user.companyId) {
      // Group attachment. Two shapes:
      //   - Full property bag when ``user.company`` is populated
      //     (PR-5's ``/auth/me`` returns it for company users).
      //   - Bare attachment (id only) during the login-flow tick where
      //     ``setUserFromLogin`` runs before the background
      //     ``getMe()`` resolves and hydrates the full company object.
      //     The follow-up identify+group fires automatically when the
      //     ``user`` reference changes; PostHog merges group props.
      if (user.company) {
        const group: GroupProperties = {
          name: user.company.name,
          slug: user.company.slug,
          status: user.company.status,
          setup_complete: user.company.setup_complete,
          marketing_licenses: user.company.marketing_licenses,
          company_modules: user.company.company_modules,
          pilot_cohort: derivePilotCohort(user.company),
        };
        if (user.company.country) group.country = user.company.country;
        if (user.company.timezone) group.timezone = user.company.timezone;
        if (user.company.sso_mode) group.sso_mode = user.company.sso_mode;
        posthog.group("company", user.companyId, group);
      } else {
        posthog.group("company", user.companyId, {});
      }
    }
  }, [isEnabled, posthog, user]);

  return <>{children}</>;
}
